import { CANVAS_WIDTH, CANVAS_HEIGHT, PLAYER_SPEED, BULLET_SPEED, PLAYER_START_LIVES } from '../config/constants.js';
import { getSpecialAbility } from './SpecialAbilities.js';

export class Player {
  constructor(character, x, y) {
    this.character = character;
    this.x = x;
    this.y = y;
    this.width = 49; // Increased by 5% from 47px (47 * 1.05 = 49.35 → 49) for visual sprite
    this.height = 49; // Increased by 5% from 47px (47 * 1.05 = 49.35 → 49) for visual sprite
    this.hitboxWidth = 24; // Tighter hitbox for bullet-hell grazing (about 50% of sprite size)
    this.hitboxHeight = 24; // Tighter hitbox for bullet-hell grazing (about 50% of sprite size)
    this.speed = character.speed || PLAYER_SPEED;
    this.lives = PLAYER_START_LIVES; // Start with 5 hearts
    this.score = 0;
    this.specialCharge = 0;
    this.specialsBanked = 0; // Number of specials banked (0-3)
    this.specialActive = false;
    this.specialAbility = null; // Current active special ability instance
    this.specialStartTime = 0; // When the special was activated
    this.gameEngine = null; // Reference to game engine (set externally)
    this.invincible = false;
    this.lastFireTime = 0;
    this.fireRate = (1000 / (character.fireRate || 3)); // Convert shots/sec to ms
    this.image = new Image();
    this.image.src = character.sprite;
    this.vx = 0;
    this.vy = 0;
    this.facingRight = true; // Track sprite facing direction
  }

  update(deltaTime, input) {
    // Movement calculation to match original game feel
    // Use deltaTime directly (in ms) with a scale factor for smooth movement
    const moveScale = 0.0007; // Increased for faster, more responsive arcade-style movement

    // Movement from input
    this.vx = 0;
    this.vy = 0;

    if (input.left) this.vx = -this.speed;
    if (input.right) this.vx = this.speed;
    if (input.up) this.vy = -this.speed;
    if (input.down) this.vy = this.speed;

    // Apply joystick
    if (input.joystick) {
      this.vx = input.joystick.x * this.speed;
      this.vy = input.joystick.y * this.speed;
    }

    // Update position with scaled movement for smooth, slower feel
    this.x += this.vx * moveScale * deltaTime;
    this.y += this.vy * moveScale * deltaTime;

    // Update facing direction based on horizontal movement
    if (this.vx > 0) {
      this.facingRight = true;
    } else if (this.vx < 0) {
      this.facingRight = false;
    }

    // Generate particle trails when moving - fixed for consistent positioning
    const isMoving = Math.abs(this.vx) > 0 || Math.abs(this.vy) > 0;
    if (isMoving && this.gameEngine && this.gameEngine.particles) {
      // Create 2-3 trail particles with character-specific color
      // Position trails consistently behind the character regardless of facing direction
      for (let i = 0; i < 3; i++) {
        const particle = {
          x: this.x + (Math.random() - 0.5) * 8, // Slightly tighter spread
          y: this.y + this.height / 2 + (Math.random() - 0.5) * 4, // Behind/below character
          vx: (Math.random() - 0.5) * 0.1, // Minimal horizontal drift
          vy: 0.2 + Math.random() * 0.15, // Consistent downward trail
          life: 0.3 + Math.random() * 0.2,
          decay: 0.015 + Math.random() * 0.01,
          size: 2,
          color: this.character.particleColor || '#7fd0ff'
        };
        this.gameEngine.particles.push(particle);
      }
    }

    // Bounds checking - Different for Cloaks vs Migos
    if (this.character.wrapHorizontal) {
      // CLOAKS: Horizontal wrap (Pac-Man style)
      if (this.x < -this.width / 2) {
        this.x = CANVAS_WIDTH + this.width / 2;
      } else if (this.x > CANVAS_WIDTH + this.width / 2) {
        this.x = -this.width / 2;
      }
      // Normal vertical bounds (restricted to lower portion of screen)
      const minY = CANVAS_HEIGHT - 220;
      const maxY = CANVAS_HEIGHT - 30;
      this.y = Math.max(minY, Math.min(maxY, this.y));
    } else if (this.character.fullVertical) {
      // MIGOS: Full vertical range, no horizontal wrap
      this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
      // Full vertical range (can go from near top to near bottom)
      const minY = 60;
      const maxY = CANVAS_HEIGHT - 30;
      this.y = Math.max(minY, Math.min(maxY, this.y));
    } else {
      // Fallback: Normal bounds
      this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
      this.y = Math.max(this.height / 2, Math.min(CANVAS_HEIGHT - this.height / 2, this.y));
    }

    // Charge special ability (reduced from 0.5 to 0.25 to make it harder to fill)
    if (!this.specialActive) {
      // Only charge if we haven't reached the max (3 specials banked)
      if (this.specialsBanked < 3) {
        this.specialCharge += 0.25;

        // Auto-bank when fully charged (up to 3 specials max)
        if (this.specialCharge >= 100) {
          this.specialsBanked++;
          this.specialCharge = 0; // Reset and continue charging
        }
      } else {
        // At max capacity (3 banked) - stop charging, cap at 100
        this.specialCharge = Math.min(100, this.specialCharge);
      }
    }

    // Update active special ability
    if (this.specialActive && this.specialAbility && this.specialAbility.update) {
      this.specialAbility.update(this, this.gameEngine, deltaTime);
    }
  }

  canFire(currentTime) {
    return currentTime - this.lastFireTime > this.fireRate;
  }

  fire(currentTime) {
    this.lastFireTime = currentTime;

    // Default bullet
    const bullet = {
      x: this.x,
      y: this.y - this.height / 2,
      vx: 0,
      vy: -this.character.bulletSpeed || -BULLET_SPEED,
      damage: this.specialActive ? 2 : 1,
      friendly: true,
      projectileUrl: this.character.projectile // Character-specific projectile sprite
    };

    // Note: Some special abilities override the fire() method entirely
    // to return arrays of bullets (spread shots, etc.)
    return bullet;
  }

  activateSpecial() {
    // Use banked special (must have at least 1 banked)
    if (this.specialsBanked > 0 && !this.specialActive) {
      this.specialsBanked--; // Use one banked special
      this.specialActive = true;
      this.specialStartTime = Date.now();

      // Get and activate the character's special ability
      const specialId = this.character.specialId;
      this.specialAbility = getSpecialAbility(specialId);

      if (this.specialAbility && this.specialAbility.activate) {
        this.specialAbility.activate(this, this.gameEngine);
      }

      // Auto-deactivate after duration
      const duration = this.specialAbility ? this.specialAbility.duration : 5000;
      setTimeout(() => {
        this.deactivateSpecial();
      }, duration);

      return true;
    }
    return false;
  }

  deactivateSpecial() {
    if (this.specialAbility && this.specialAbility.deactivate) {
      this.specialAbility.deactivate(this, this.gameEngine);
    }
    this.specialActive = false;
    this.specialAbility = null;
  }

  takeDamage() {
    if (this.invincible || this.specialActive) return false;
    this.lives--;
    this.invincible = true;
    setTimeout(() => { this.invincible = false; }, 2000);
    return true;
  }

  addLife() {
    this.lives++;
  }

  addScore(points) {
    this.score += points;
  }

  render(ctx) {
    ctx.save();

    // Flashing effect when invincible
    if (this.invincible && Math.floor(Date.now() / 100) % 2 === 0) {
      ctx.globalAlpha = 0.5;
    }

    // Special charge glow - increases intensity as special charges
    if (this.specialCharge > 0 && !this.specialActive) {
      const glowIntensity = this.specialCharge / 100; // 0 to 1
      const glowRadius = 10 + (glowIntensity * 15); // 10-25 pixels

      ctx.shadowColor = '#ffe38f'; // Gold glow for charging
      ctx.shadowBlur = glowRadius;

      // Pulse effect when fully charged
      if (this.specialCharge >= 100) {
        const pulse = Math.sin(Date.now() / 150) * 0.3 + 0.7;
        ctx.shadowBlur = glowRadius * pulse;
      }
    }

    // Special ability glow (when active)
    if (this.specialActive) {
      ctx.shadowColor = this.character.particleColor || '#7fd0ff';
      ctx.shadowBlur = 20;
    }

    // Flip sprite horizontally when moving left - fixed to flip around center
    if (!this.facingRight) {
      // Translate to sprite center, flip, then translate back
      ctx.translate(this.x, this.y);
      ctx.scale(-1, 1);
      ctx.translate(-this.x, -this.y);
    }

    ctx.drawImage(
      this.image,
      this.x - this.width / 2,
      this.y - this.height / 2,
      this.width,
      this.height
    );

    ctx.restore();

    // Render special ability effects (if any)
    if (this.specialActive && this.specialAbility && this.specialAbility.render) {
      this.specialAbility.render(ctx, this, this.gameEngine);
    }
  }
}
